import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { useEffect, useState } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const [activeSlug, setActiveSlug] = useState(slug || '');

  useEffect(() => {
    setActiveSlug(slug || '');
  }, [slug]);

  const articles = getArticlesByCategory(activeSlug);
  const currentCategory = getCategoryBySlug(activeSlug);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">
            分类未找到
          </h2>
          <p className="text-foreground-500 text-sm mb-4">请检查分类链接是否正确</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const pageTitle = currentCategory.name;
  const pageDesc = `${currentCategory.name} - ${currentCategory.description}。欧美专区为您精选${currentCategory.name}相关内容，涵盖最新资讯、深度解析与趋势报道。`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`欧美专区,${currentCategory.name},欧美精选内容`}
        canonicalUrl={`https://www.oumeizone.com/category/${activeSlug}`}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory.name} | 欧美专区`,
          url: `https://www.oumeizone.com/category/${activeSlug}`,
          description: pageDesc,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />

            {/* Category Header */}
            <div className="relative rounded-lg overflow-hidden h-48 md:h-64 mb-10">
              <img
                src={currentCategory.image}
                alt={currentCategory.name}
                title={`${currentCategory.name} - 欧美专区`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className={`${currentCategory.icon} text-2xl text-background-50`}></i>
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                    {currentCategory.name}
                  </h1>
                </div>
                <p className="text-background-200 text-sm md:text-base max-w-2xl">
                  {currentCategory.description}
                </p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-label text-sm transition-all duration-200 whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon}`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Articles Grid */}
            <section className="pb-16">
              {articles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {articles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                  <p className="text-foreground-500 text-sm">该分类暂无文章</p>
                </div>
              )}
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}